import React from 'react';

/**
 * WHAT: Curated IANA timezone list + a reusable <select> for a monitored source
 *       connection's "Source Timezone".
 * WHY:  DAMA converts source-database timestamps (last_login, created_date, etc.) to
 *       UTC at ingest using this per-connection timezone (see
 *       docs/DESIGN_timezone_utc_normalization_20261002.md, D3). Defaulting to UTC
 *       keeps today's behavior (no shift) until a DBA sets the real zone of a source
 *       that stores local time. Shared across all 5 per-tech connection forms so the
 *       option list lives in exactly one place (single-source rule).
 */

// Curated common zones (UTC first/default). IANA names — these are what the backend
// feeds to normalize_source_to_utc(). Keep UTC at the top.
export const SOURCE_TIMEZONES = [
  { value: 'UTC', label: 'UTC (default)' },
  { value: 'America/New_York', label: 'US Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'US Central (America/Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'US Arizona, no DST (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'US Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'US Hawaii (Pacific/Honolulu)' },
  { value: 'America/Sao_Paulo', label: 'Brazil (America/Sao_Paulo)' },
  { value: 'Europe/London', label: 'UK (Europe/London)' },
  { value: 'Europe/Paris', label: 'Central Europe (Europe/Paris)' },
  { value: 'Europe/Frankfurt', label: 'Germany (Europe/Frankfurt)' },
  { value: 'Asia/Dubai', label: 'Gulf (Asia/Dubai)' },
  { value: 'Asia/Kolkata', label: 'India (Asia/Kolkata)' },
  { value: 'Asia/Singapore', label: 'Singapore (Asia/Singapore)' },
  { value: 'Asia/Shanghai', label: 'China (Asia/Shanghai)' },
  { value: 'Asia/Tokyo', label: 'Japan (Asia/Tokyo)' },
  { value: 'Australia/Sydney', label: 'Australia East (Australia/Sydney)' },
];

/**
 * A labeled Source Timezone <select>. Props:
 *   value     - current source_timezone (defaults handled by caller; 'UTC' if empty)
 *   onChange  - (newValue) => void
 *   disabled  - view-mode flag (matches sibling fields)
 */
export function SourceTimezoneSelect({ value, onChange, disabled }) {
  return (
    <div className="mb-3">
      <label className="form-label">Source Timezone</label>
      <select
        className="form-select"
        value={value || 'UTC'}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {SOURCE_TIMEZONES.map((tz) => (
          <option key={tz.value} value={tz.value}>{tz.label}</option>
        ))}
      </select>
      <small className="text-muted">
        Timezone the monitored database records its timestamps in. DAMA converts them to
        UTC on collection. Leave as UTC unless this source stores local time.
      </small>
    </div>
  );
}

export default SourceTimezoneSelect;
